import { observer } from 'mobx-react'
import React, { lazy, ReactElement } from 'react'

import { PinPipelineItemsCountController } from '@flowwai-frontend/bps-officer-web/common/ui'
import { GlobalSearchModal } from '@flowwai-frontend/bps-officer-web/global-search'
import {
   ContactTemplateAPIController,
   useSetUserIdForMicrosoftClarity,
   useUserAppConfigStore,
   useIsDashboardAdmin,
   withCurrentUserDetails,
   withUserSpecificAccessData
} from '@flowwai-frontend/bps-officer-web/iam'
import { Config } from '@flowwai-frontend/bps-officer-web/env-config'
import {
   HomeNavbarController,
   isPinnedBoardsEnabled
} from '@flowwai-frontend/bps-officer-web/navbar'
import { usePipelineStore } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import HomeNestedRoutes from '../HomeNestedRoutes/HomeNestedRoutes'

const RoutesWithSidebar = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/navbar-base').then(module => ({
      default: module.RoutesWithSidebar
   }))
)

const AccountBoardsController = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.AccountBoardsController
   }))
)

const NotificationsStoreProvider = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/notifications').then(module => ({
      default: module.NotificationsStoreProvider
   }))
)

const TemplatesBaseDetailsAPIController = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/navbar').then(module => ({
      default: module.TemplatesBaseDetailsAPIController
   }))
)

const TemplateBoardsController = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.TemplateBoardsController
   }))
)

const UnReadNotificationsCountController = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/notifications').then(module => ({
      default: module.UnReadNotificationsCountController
   }))
)

const TemplateBoardsInfoAPIController = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.TemplateBoardsInfoAPIController
   }))
)

const HomeRouteAPIController = lazy(() =>
   import('@flowwai-frontend/bps-officer-web/common/ui').then(module => ({
      default: module.HomeRouteAPIController
   }))
)

const HomeRoute = (): ReactElement => {
   useSetUserIdForMicrosoftClarity()
   const userAppConfigStore = useUserAppConfigStore()
   const isDashboardAdmin = useIsDashboardAdmin()

   const pipelineStore = usePipelineStore()

   const renderHomeSidebar = (): ReactElement => <HomeNavbarController />

   const renderHomeNestedRoutes = (): ReactElement => {
      const isSchedulesEnabled =
         userAppConfigStore.permissions?.isSchedulingEnabled &&
         Config.ENABLE_HOME_BOARD_ROUTE === 'true'

      const firstPipelineId = userAppConfigStore.salesPipelinesIds?.[0]

      const firstTemplateId = firstPipelineId
         ? pipelineStore.getPipeline(firstPipelineId).pipelineItemTemplateId
         : ''

      const shouldRedirectToBoards = !isSchedulesEnabled && firstTemplateId

      if (shouldRedirectToBoards) {
         return (
            <ContactTemplateAPIController>
               <TemplateBoardsController templateId={firstTemplateId}>
                  <HomeNestedRoutes />
               </TemplateBoardsController>
            </ContactTemplateAPIController>
         )
      }

      return (
         <ContactTemplateAPIController>
            <HomeNestedRoutes />
         </ContactTemplateAPIController>
      )
   }

   const renderRoutesWithSideBar = (): React.ReactElement => (
      <AccountBoardsController>
         <>
            <RoutesWithSidebar
               sidebarComponent={renderHomeSidebar}
               routeComponent={renderHomeNestedRoutes}
            />
            {!isDashboardAdmin && <GlobalSearchModal />}
         </>
      </AccountBoardsController>
   )

   const renderHomeRoutesBasedOnFeatureToggle = (): React.ReactElement => {
      switch (true) {
         case isPinnedBoardsEnabled: {
            return (
               <PinPipelineItemsCountController>
                  {renderRoutesWithSideBar()}
               </PinPipelineItemsCountController>
            )
         }

         default: {
            return renderRoutesWithSideBar()
         }
      }
   }

   return (
      <TemplatesBaseDetailsAPIController>
         <TemplateBoardsInfoAPIController>
            <HomeRouteAPIController>
               <NotificationsStoreProvider>
                  <UnReadNotificationsCountController>
                     {renderHomeRoutesBasedOnFeatureToggle()}
                  </UnReadNotificationsCountController>
               </NotificationsStoreProvider>
            </HomeRouteAPIController>
         </TemplateBoardsInfoAPIController>
      </TemplatesBaseDetailsAPIController>
   )
}

export default withCurrentUserDetails(
   withUserSpecificAccessData(observer(HomeRoute))
)
